import { Link } from 'react-router-dom';
import { usePageMeta } from '../lib/meta';
import { CONTACT_EMAIL } from '../config';

const EFFECTIVE_DATE = 'July 18, 2026';

export function PrivacyPage() {
  usePageMeta({
    title: 'Privacy Policy',
    description:
      'How PeacockOmlettes.com handles personal data, advertising cookies, consent, and your privacy rights under GDPR, UK GDPR, and US state privacy laws.',
    path: '/privacy',
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-3xl text-plume-950 sm:text-5xl">Privacy Policy</h1>
      <p className="mt-3 text-sm text-plume-600">Effective date: {EFFECTIVE_DATE}</p>

      <div className="prose-peacock mt-8 space-y-8 leading-relaxed text-plume-900">
        <section>
          <h2 className="font-display text-2xl text-plume-950">Who we are</h2>
          <p className="mt-3">
            PeacockOmlettes.com (&ldquo;we&rdquo;, &ldquo;us&rdquo;) is a personally operated humor
            and recipe website. For any privacy matter, contact{' '}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-semibold underline underline-offset-4"
            >
              {CONTACT_EMAIL}
            </a>
            . For visitors in the EEA, UK, or Switzerland, the site operator is the data controller
            for the limited processing described below.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">What we collect</h2>
          <p className="mt-3">
            The site has no user accounts, no comment system, no newsletter, and no contact forms.
            We do not directly collect, store, or process your personal information on any server we
            operate. The site is delivered as static files from a content delivery network (Amazon
            CloudFront), which processes IP addresses transiently as a technical necessity of
            serving web content, and produces aggregate access logs used solely for security and
            capacity purposes.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">Advertising</h2>
          <p className="mt-3">
            We display advertising through Google AdSense to cover hosting costs. When ads are
            served, Google and its certified advertising partners may process personal data (such as
            cookies, device identifiers, IP address, and ad-interaction data) as described in{' '}
            <a
              href="https://policies.google.com/technologies/partner-sites"
              rel="noopener noreferrer"
              className="font-semibold underline underline-offset-4"
            >
              How Google uses information from sites that use its services
            </a>
            .
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-6">
            <li>
              <strong>EEA, UK, and Switzerland:</strong> no advertising cookies are set and no ad
              personalization occurs unless you affirmatively consent through the Google-certified
              consent message (IAB Transparency &amp; Consent Framework) shown on your first visit.
              You can change or withdraw your choices at any time on the{' '}
              <Link to="/privacy-settings" className="font-semibold underline underline-offset-4">
                Privacy Settings
              </Link>{' '}
              page.
            </li>
            <li>
              <strong>United States and elsewhere:</strong> ads may be served and measured by
              default, consistent with applicable opt-out privacy regimes. You may opt out of
              personalized advertising at any time via{' '}
              <Link to="/privacy-settings" className="font-semibold underline underline-offset-4">
                Privacy Settings
              </Link>{' '}
              or Google&rsquo;s{' '}
              <a
                href="https://adssettings.google.com"
                rel="noopener noreferrer"
                className="font-semibold underline underline-offset-4"
              >
                Ads Settings
              </a>
              .
            </li>
            <li>
              <strong>Global Privacy Control:</strong> we honor the GPC browser signal as a valid
              opt-out of the sale or sharing of personal information. When your browser sends GPC,
              our consent defaults deny advertising storage and personalization automatically,
              everywhere.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">Legal bases (GDPR / UK GDPR)</h2>
          <p className="mt-3">
            Where GDPR or UK GDPR applies, advertising cookies and personalization rely on your{' '}
            <strong>consent</strong> (Art. 6(1)(a)), which you may withdraw at any time with no
            effect on your use of the site. Serving the website itself, including transient IP
            processing by our CDN, relies on <strong>legitimate interest</strong> (Art. 6(1)(f)) in
            operating a secure, functional website.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">Your rights</h2>
          <p className="mt-3">
            Depending on where you live, you may have rights to access, correct, delete, or receive
            a copy of personal data, to object to or restrict processing, to opt out of the sale or
            sharing of personal information (including via GPC), and to not be discriminated against
            for exercising these rights. Because we hold no personal data about visitors ourselves,
            most requests are best directed to Google using the tools linked above; for anything
            else, or for help, email{' '}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-semibold underline underline-offset-4"
            >
              {CONTACT_EMAIL}
            </a>
            . EEA/UK visitors may also lodge a complaint with their local supervisory authority.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">International transfers</h2>
          <p className="mt-3">
            The site is hosted in the United States. Where Google processes data of EEA/UK/Swiss
            visitors, those transfers occur under Google&rsquo;s applicable safeguards, including
            the EU-U.S. Data Privacy Framework and standard contractual clauses.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">Children</h2>
          <p className="mt-3">
            The site is a general-audience publication and is not directed to children under 13 (or
            the applicable age of digital consent in your jurisdiction). We do not knowingly collect
            personal information from children.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-plume-950">Changes</h2>
          <p className="mt-3">
            We will post any changes to this policy on this page with an updated effective date.
            Material changes to how advertising or consent works will be reflected in the consent
            tools themselves.
          </p>
        </section>
      </div>
    </div>
  );
}
